// documented in: docs/overview/subsystems/documents/document_system.md - Chat input with document attachment
// documented in: docs/overview/subsystems/documents/in_chat_doc_upload.md - Frontend document attachment interface
import React, { memo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Card, CircularProgress, Grid2, IconButton, Stack, Switch, TextField, Tooltip, Typography, useTheme, Chip, Alert, Box } from "@mui/material";
import { icon } from "@/app/style/icons";
import { useMutation, useQuery } from "@apollo/client";
import { MessageType, Mutation, Query } from "@/app/graphql/codegen";
import { SEND_MESSAGE } from "@chat/graphql/mutations/sendMessage";
import { EDIT_CHAT_SETTINGS } from "@company/graphql/mutations/updateChatSettings";
import { FETCH_CHAT_SETTINGS } from "@chat/graphql/queries/fetchChatSettings";
import { RouteToSettings } from "@routing/RouteMap";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/app/store";
import { setInputValue, clearInputValue, addPendingSentMessage } from "@store/slices/chat";
import { addAttachment, removeAttachment, clearAttachments, setUploading, setUploadError, clearUploadError } from "@store/slices/chatAttachment";
import AnimatedSearch from "@chat/components/main/AnimatedSearch";
import { FETCH_CONTEXT_FOR_CHAT } from "@chat/graphql/queries/fetchContextForChat";
import axios from "axios";
import { LocalStorage } from "@/types/enums";
import AttachmentPreview from "@chat/components/AttachmentPreview";

interface ChatInputProps {
    disabled?: boolean;
    id: string;
    searchValue: string;
    setSearchValue: (value: string) => void;
    isGenerating?: boolean;
}

const ChatInput = memo(({ disabled, id, isGenerating, searchValue, setSearchValue }: ChatInputProps) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const inputValue = useSelector((state: RootState) => state.chat.inputValues[id] || "");
    const attachments = useSelector((state: RootState) => state.chatAttachment.attachments[id] || []);
    const isUploading = useSelector((state: RootState) => state.chatAttachment.isUploading[id] || false);
    const uploadError = useSelector((state: RootState) => state.chatAttachment.uploadErrors[id]);
    const contextWarning = useSelector((state: RootState) => state.chatAttachment.contextWarnings[id]);
    
    const [isDragging, setIsDragging] = useState(false);
    const dragCounterRef = React.useRef(0);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const [sendMessageMutation] = useMutation<Pick<Mutation, "sendMessage">>(SEND_MESSAGE);

    const sendMessage = () => {
        // Guard clauses
        if (isGenerating || disabled || !inputValue.trim()) {
            return;
        }

        const optimisticId = uuidv4();
        const now = new Date().toISOString();
        const currentContent = inputValue.trim();
        const currentAttachments = [...attachments];

        // INSTANT UI UPDATE: Add pending message to Redux (renders immediately, bypasses Apollo)
        dispatch(addPendingSentMessage({
            chatId: id,
            message: {
                id: optimisticId,
                content: currentContent,
                createdAt: now,
                type: 'USER',
                chatId: id,
                senderId: '', // Will be filled by server
                attachments: currentAttachments.map(att => ({
                    fileName: att.fileName,
                    tokens: att.tokens,
                    fileSize: att.size,
                })),
            },
        }));

        // Clear input and attachments AFTER adding to pending (so we capture the right values)
        dispatch(clearInputValue(id));
        if (currentAttachments.length > 0) {
            dispatch(clearAttachments(id));
        }

        sendMessageMutation({
            variables: {
                chatId: id,
                content: currentContent,
                attachments: currentAttachments.map(att => ({
                    fileName: att.fileName,
                    content: att.content,
                    tokens: att.tokens,
                    fileSize: att.size
                })),
            },
            // No optimisticResponse needed - we're using Redux pending messages instead
            onError: (error) => {
                console.error("Error sending message:", error);
                // TODO: Could add error handling here to remove the pending message if send fails
            },
        });
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            if (!event.shiftKey) {
                event.preventDefault();
                // Add the same safety checks here
                if (!isGenerating && !disabled && inputValue.trim()) {
                    sendMessage();
                }
            }
        }
    };

    const { data } = useQuery<Pick<Query, "chat">>(FETCH_CHAT_SETTINGS, {
        variables: { id },
        skip: !id,
    });

    const [editSettings] = useMutation<Pick<Mutation, "editChatSettings">, { id: string; companyContextEnabled: boolean }>(EDIT_CHAT_SETTINGS);

    // Get company context from direct field first, then fallback to settings object
    const isCompanyContextEnabled = data?.chat?.companyContextEnabled || (data?.chat?.settings as any)?.companyContextEnabled || false;

    const toggleCompanyContext = async () => {
        if (!id) return;

        await editSettings({
            variables: {
                id,
                companyContextEnabled: !isCompanyContextEnabled,
            },
            refetchQueries: [
                {
                    query: FETCH_CHAT_SETTINGS,
                    variables: { id },
                },
                {
                    query: FETCH_CONTEXT_FOR_CHAT,
                    variables: { chatId: id },
                },
            ],
        });
    };

    // Supported file types
    const SUPPORTED_FILE_TYPES = [
        'application/pdf',
        'text/plain',
        'text/markdown',
        'text/csv',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
    ];

    const SUPPORTED_EXTENSIONS = ['.pdf', '.txt', '.md', '.csv', '.xlsx', '.xls'];

    // File upload handler
    const handleFileUpload = async (file: File) => {
        // Validate file type
        const fileName = file.name.toLowerCase();
        const hasValidExtension = SUPPORTED_EXTENSIONS.some(ext => fileName.endsWith(ext));
        const hasValidMimeType = SUPPORTED_FILE_TYPES.includes(file.type);

        if (!hasValidExtension && !hasValidMimeType) {
            dispatch(setUploadError({ 
                chatId: id, 
                error: "Nur PDF, TXT, MD, CSV, XLSX/XLS Dateien sind erlaubt" 
            }));
            return;
        }

        // Validate file size (10MB)
        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
            dispatch(setUploadError({ chatId: id, error: "Datei ist zu groß. Maximum ist 10MB" }));
            return;
        }

        try {
            dispatch(setUploading({ chatId: id, isUploading: true }));
            dispatch(clearUploadError(id));

            // Get auth token
            const token = localStorage.getItem(LocalStorage.AUTH_TOKEN);
            if (!token) {
                throw new Error("Nicht authentifiziert");
            }

            // Create form data
            const formData = new FormData();
            formData.append("file", file);

            // Upload file
            // Use relative path so it works with nginx proxy in production and setupProxy in development
            const response = await axios.post(
                `/upload/chat/${id}/attachment`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            // Store attachment data
            dispatch(
                addAttachment({
                    chatId: id,
                    attachment: {
                        fileName: response.data.fileName,
                        tokens: response.data.tokens,
                        size: response.data.size,
                        pages: response.data.pages,
                        content: response.data.content,
                        contextInfo: response.data.contextInfo,
                    },
                })
            );
        } catch (error: any) {
            console.error("Error uploading file:", error);
            const errorMessage = error.response?.data?.details || error.response?.data?.error || "Fehler beim Hochladen der Datei";
            dispatch(setUploadError({ chatId: id, error: errorMessage }));
        } finally {
            dispatch(setUploading({ chatId: id, isUploading: false }));
        }
    };

    // Drag and drop handlers with counter to handle nested elements
    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (isGenerating || disabled) return;
        
        dragCounterRef.current++;
        
        // Check if dragging files
        if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        
        dragCounterRef.current--;
        
        // Only hide when we've left all nested elements
        if (dragCounterRef.current === 0) {
            setIsDragging(false);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Reset counter and state
        dragCounterRef.current = 0;
        setIsDragging(false);

        if (isGenerating || disabled) return;

        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            // Process all files
            for (const file of files) {
                await handleFileUpload(file);
            }
        }
    };

    const handleRemoveAttachment = (index: number) => {
        dispatch(removeAttachment({ chatId: id, index }));
        // If last attachment removed, clear errors
        if (attachments.length === 1) {
            dispatch(clearUploadError(id));
        }
    };

    const handleFileSelect = () => {
        fileInputRef.current?.click();
    };

    const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            for (let i = 0; i < files.length; i++) {
                await handleFileUpload(files[i]);
            }
        }
        // Reset the input so the same file can be selected again
        e.target.value = '';
    };

    return (
        <Card>
            <Grid2 container alignItems={"center"} justifyContent={"space-between"}>
                <Stack ml={2} direction={"row"} spacing={1} alignItems={"center"}>
                    <Tooltip title={"Chateinstellungen"} placement={"top"}>
                        <IconButton
                            size={"small"}
                            onClick={() => navigate(RouteToSettings.CHAT)}
                            disabled={disabled}
                            sx={{
                                opacity: disabled ? 0.7 : 1,
                                "&.Mui-disabled": {
                                    color: `${theme.palette.semantic.button.disabled}80`,
                                },
                            }}
                        >
                            <icon.settings sx={{ color: "semantic.icon.functional" }} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={"Dokument anhängen (PDF, TXT, MD, CSV, XLSX/XLS)"} placement={"top"}>
                        <IconButton
                            size={"small"}
                            onClick={handleFileSelect}
                            disabled={disabled || isGenerating || isUploading}
                            sx={{
                                opacity: disabled || isGenerating || isUploading ? 0.7 : 1,
                                "&.Mui-disabled": {
                                    color: `${theme.palette.semantic.button.disabled}80`,
                                },
                            }}
                        >
                            <icon.attachFile sx={{ color: "semantic.icon.functional" }} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={"Suche im Chatverlauf"} placement={"top"}>
                        <AnimatedSearch value={searchValue} setValue={setSearchValue} />
                    </Tooltip>
                </Stack>

                <Grid2 container alignItems={"center"} justifyContent={"end"} py={0.5} px={2} spacing={1}>
                    <Grid2>
                        <Typography fontSize={12}>Firmenprofil einbeziehen</Typography>
                    </Grid2>

                    <Grid2>
                        <Tooltip
                            title={
                                "Mit dem Firmenprofil-Button kannst Du dem ESGbot spezifische Informationen zu Deinem Unternehmen bereitstellen. Diese kannst Du in den Einstellungen hinterlegen, um gezieltere und individuell auf Deine Firma zugeschnittene Antworten zu erhalten."
                            }
                            placement={"top"}
                        >
                            <IconButton size={"small"}>
                                <icon.info sx={{ color: "semantic.icon.informative" }} />
                            </IconButton>
                        </Tooltip>
                    </Grid2>

                    <Grid2>
                        <Switch size={"small"} disabled={isGenerating} checked={isCompanyContextEnabled} onChange={toggleCompanyContext} />
                    </Grid2>
                </Grid2>
            </Grid2>

            <Card
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                sx={{
                    border: isDragging ? `2px dashed ${theme.palette.primary.main}` : undefined,
                    backgroundColor: isDragging ? theme.palette.action.hover : undefined,
                    transition: "all 0.2s ease",
                }}
            >
                {/* Error message */}
                {uploadError && (
                    <Grid2 px={1} pt={1}>
                        <Alert severity="error" onClose={() => dispatch(clearUploadError(id))}>
                            {uploadError}
                        </Alert>
                    </Grid2>
                )}

                {/* Context warning */}
                {contextWarning && (
                    <Grid2 px={1} pt={1}>
                        <Alert severity="warning">
                            {contextWarning}
                        </Alert>
                    </Grid2>
                )}

                {/* Attachments preview */}
                {attachments.length > 0 && (
                    <Grid2 px={1} pt={1} container spacing={1}>
                        {attachments.map((att, index) => (
                            <Grid2 key={index} size={12}>
                                <AttachmentPreview
                                    fileName={att.fileName}
                                    size={att.size}
                                    tokens={att.tokens}
                                    pages={att.pages}
                                    onRemove={() => handleRemoveAttachment(index)}
                                    disabled={isGenerating}
                                    variant="light"
                                    mode="input"
                                />
                            </Grid2>
                        ))}
                    </Grid2>
                )}

                {/* Drag overlay */}
                {isDragging && (
                    <Grid2
                        px={1}
                        pt={1}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: 100,
                        }}
                    >
                        <Stack alignItems="center" spacing={1}>
                            <Typography variant="h6" color="primary">
                                Dokumente hier ablegen
                            </Typography>
                        </Stack>
                    </Grid2>
                )}

                {/* Loading state */}
                {isUploading && (
                    <Grid2 px={1} pt={1}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1 }}>
                            <CircularProgress size={20} />
                            <Typography fontSize={13}>Dokumente werden verarbeitet...</Typography>
                        </Box>
                    </Grid2>
                )}

                <Grid2 container spacing={1} alignItems="center" px={1} pt={1} pb={0.25}>
                    <Grid2 sx={{ flexGrow: 1 }}>
                        <TextField
                            fullWidth
                            placeholder="Wobei kann ich helfen?"
                            multiline
                            maxRows={8}
                            slotProps={{ input: { sx: { px: 0.5 } } }}
                            value={inputValue}
                            onKeyDown={handleKeyDown}
                            onChange={(e) => dispatch(setInputValue({ chatId: id, value: e.target.value }))}
                            disabled={isGenerating || disabled || isUploading}
                        />
                    </Grid2>

                    <Grid2 sx={{ py: 1 }}>
                        {isGenerating ? (
                            <CircularProgress size={20} sx={{ my: 1, mx: 1.5, color: "semantic.button.primary" }} />
                        ) : (
                            <IconButton onClick={sendMessage} disabled={disabled}>
                                <icon.send sx={{ color: "semantic.icon.send" }} />
                            </IconButton>
                        )}
                    </Grid2>
                </Grid2>

                <Grid2 pb={0.5}>
                    <Typography fontSize={10} fontStyle={"italic"} align={"center"}>
                        Der ESGbot bietet keine aktive Rechtsberatung. Es können Fehler passieren. Wichtige Informationen müssen immer überprüft werden.
                    </Typography>
                </Grid2>
            </Card>

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.txt,.md,.csv,.xlsx,.xls,application/pdf,text/plain,text/markdown,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
            />
        </Card>
    );
});

export default ChatInput;
